import { getDeviceEnv } from "@frontend/utils/device.utils";
import { useCallback, useEffect, useState } from "react";
import {
	type InstallOutcome,
	isInstallPromptAvailable,
	isRunningStandalone,
	showInstallPrompt,
	subscribeInstallPrompt,
	wasInstalledThisVisit,
} from "../pwa/install_prompt";

export type InstallResult = InstallOutcome | "guide";

export interface PwaInstallState {
	/** True when Chromium's beforeinstallprompt has fired and we can install. */
	isInstallAvailable: boolean;
	/** True when running as an installed PWA (display-mode standalone). */
	isStandalone: boolean;
	/** True on iOS/iPadOS — no beforeinstallprompt, needs manual Add to Home Screen. */
	isAppleMobile: boolean;
	/**
	 * Fires the browser's install dialog where one exists. `guide` means the
	 * platform has no prompt (Apple) and the caller should show the steps;
	 * `unavailable` means Chromium has not offered one on this page (already
	 * installed, or installability unmet) and the browser menu is the way.
	 */
	install: () => Promise<InstallResult>;
}

const read = () => ({
	isInstallAvailable: isInstallPromptAvailable(),
	isStandalone: isRunningStandalone() || wasInstalledThisVisit(),
});

/**
 * React view of `pwa/install_prompt.ts`: the captured prompt, whether the
 * app already runs installed, and the platform. State comes from the
 * module's listeners, so every component sees the same answer.
 */
export const usePwaInstall = (): PwaInstallState => {
	const [state, setState] = useState(read);
	useEffect(() => subscribeInstallPrompt(() => setState(read())), []);

	const install = useCallback(async (): Promise<InstallResult> => {
		if (getDeviceEnv().isIOS) return "guide";
		const outcome = await showInstallPrompt();
		setState(read());
		return outcome;
	}, []);

	return { ...state, isAppleMobile: getDeviceEnv().isIOS, install };
};
